// -*- coding: utf-8 -*-
/**
 * Apollo - Test Explanations Data (Bilingual FR/EN)
 * Detailed explanations for all personality test results
 */

// DISC Colors (same for both languages)
export const DISC_COLORS = {
  D: '#EF4444', // Red
  I: '#F59E0B', // Yellow/Orange
  S: '#10B981', // Green
  C: '#3B82F6', // Blue
};

// DISC Data by language
const DISC_DATA_FR = {
  colors: DISC_COLORS,
  labels: {
    D: 'Dominance',
    I: 'Influence',
    S: 'Stabilité',
    C: 'Conformité',
  },
  descriptions: {
    D: {
      title: 'Dominance',
      subtitle: 'Orienté résultats et défis',
      traits: ['Direct et décisif', 'Orienté objectifs', 'Aime les défis', 'Prend des risques', 'Leader naturel'],
      strengths: 'Capacité à prendre des décisions rapides, résoudre les problèmes, atteindre les objectifs.',
      communication: 'Préfère une communication directe, concise et orientée vers l\'action.',
      workStyle: 'Travaille mieux avec autonomie et responsabilité, aime avoir le contrôle.',
    },
    I: {
      title: 'Influence',
      subtitle: 'Orienté personnes et enthousiasme',
      traits: ['Enthousiaste et optimiste', 'Persuasif', 'Sociable', 'Créatif', 'Collaboratif'],
      strengths: 'Capacité à motiver les autres, créer des relations, générer de l\'enthousiasme.',
      communication: 'Préfère une communication chaleureuse, interactive et positive.',
      workStyle: 'Travaille mieux en équipe, dans un environnement dynamique et stimulant.',
    },
    S: {
      title: 'Stabilité',
      subtitle: 'Orienté harmonie et constance',
      traits: ['Patient et calme', 'Fiable', 'Loyal', 'À l\'écoute', 'Méthodique'],
      strengths: 'Capacité à maintenir la stabilité, soutenir les autres, travailler en équipe.',
      communication: 'Préfère une communication calme, respectueuse et personnelle.',
      workStyle: 'Travaille mieux dans un environnement stable et prévisible, avec des relations de confiance.',
    },
    C: {
      title: 'Conformité',
      subtitle: 'Orienté qualité et précision',
      traits: ['Analytique', 'Précis', 'Systématique', 'Perfectionniste', 'Prudent'],
      strengths: 'Capacité à analyser, garantir la qualité, respecter les processus.',
      communication: 'Préfère une communication factuelle, détaillée et structurée.',
      workStyle: 'Travaille mieux avec des instructions claires, du temps pour l\'analyse et des standards élevés.',
    },
  },
};

const DISC_DATA_EN = {
  colors: DISC_COLORS,
  labels: {
    D: 'Dominance',
    I: 'Influence',
    S: 'Steadiness',
    C: 'Conscientiousness',
  },
  descriptions: {
    D: {
      title: 'Dominance',
      subtitle: 'Results and challenge-oriented',
      traits: ['Direct and decisive', 'Goal-oriented', 'Loves challenges', 'Risk-taker', 'Natural leader'],
      strengths: 'Ability to make quick decisions, solve problems, achieve objectives.',
      communication: 'Prefers direct, concise, action-oriented communication.',
      workStyle: 'Works best with autonomy and responsibility, likes to be in control.',
    },
    I: {
      title: 'Influence',
      subtitle: 'People and enthusiasm-oriented',
      traits: ['Enthusiastic and optimistic', 'Persuasive', 'Sociable', 'Creative', 'Collaborative'],
      strengths: 'Ability to motivate others, build relationships, generate enthusiasm.',
      communication: 'Prefers warm, interactive and positive communication.',
      workStyle: 'Works best in teams, in a dynamic and stimulating environment.',
    },
    S: {
      title: 'Steadiness',
      subtitle: 'Harmony and consistency-oriented',
      traits: ['Patient and calm', 'Reliable', 'Loyal', 'Good listener', 'Methodical'],
      strengths: 'Ability to maintain stability, support others, work in teams.',
      communication: 'Prefers calm, respectful and personal communication.',
      workStyle: 'Works best in a stable and predictable environment, with trusting relationships.',
    },
    C: {
      title: 'Conscientiousness',
      subtitle: 'Quality and precision-oriented',
      traits: ['Analytical', 'Precise', 'Systematic', 'Perfectionist', 'Cautious'],
      strengths: 'Ability to analyze, ensure quality, follow processes.',
      communication: 'Prefers factual, detailed and structured communication.',
      workStyle: 'Works best with clear instructions, time for analysis and high standards.',
    },
  },
};

// Big Five Data by language
const BIGFIVE_DATA_FR = {
  labels: {
    O: 'Ouverture',
    C: 'Conscienciosité',
    E: 'Extraversion',
    A: 'Agréabilité',
    N: 'Neuroticisme',
  },
  fullLabels: {
    O: 'Ouverture à l\'expérience',
    C: 'Conscienciosité',
    E: 'Extraversion',
    A: 'Agréabilité',
    N: 'Stabilité émotionnelle (inverse du Neuroticisme)',
  },
  descriptions: {
    O: {
      high: 'Curieux, créatif, ouvert aux nouvelles idées et expériences. Apprécie l\'art, l\'imagination et la diversité.',
      low: 'Pragmatique, préfère la routine et les méthodes éprouvées. Valorise la tradition et le concret.',
      business: 'En affaires : apporte innovation et pensée créative, mais peut parfois manquer de focus sur l\'exécution.',
    },
    C: {
      high: 'Organisé, fiable, orienté objectifs. Planifie et termine ce qu\'il commence.',
      low: 'Flexible, spontané, adaptable. Préfère improviser plutôt que planifier.',
      business: 'En affaires : garantit la qualité et le respect des délais, pilier de la fiabilité.',
    },
    E: {
      high: 'Sociable, énergique, expressif. Aime interagir et être au centre de l\'action.',
      low: 'Réservé, réfléchi, indépendant. Préfère les interactions en petit groupe ou le travail individuel.',
      business: 'En affaires : excellent pour le networking et la représentation, ou la concentration et l\'analyse profonde.',
    },
    A: {
      high: 'Coopératif, empathique, soucieux des autres. Recherche l\'harmonie et évite les conflits.',
      low: 'Compétitif, direct, objectif. N\'hésite pas à défendre ses positions fermement.',
      business: 'En affaires : facilite la collaboration ou pousse la performance et les négociations dures.',
    },
    N: {
      high: 'Sensible aux stress, ressent intensément les émotions. Peut anticiper les risques mais aussi s\'inquiéter.',
      low: 'Stable émotionnellement, résilient, calme sous pression. Gère bien les situations difficiles.',
      business: 'En affaires : stabilité émotionnelle = calme en crise, sensibilité = empathie et vigilance.',
    },
  },
};

const BIGFIVE_DATA_EN = {
  labels: {
    O: 'Openness',
    C: 'Conscientiousness',
    E: 'Extraversion',
    A: 'Agreeableness',
    N: 'Neuroticism',
  },
  fullLabels: {
    O: 'Openness to Experience',
    C: 'Conscientiousness',
    E: 'Extraversion',
    A: 'Agreeableness',
    N: 'Emotional Stability (inverse of Neuroticism)',
  },
  descriptions: {
    O: {
      high: 'Curious, creative, open to new ideas and experiences. Appreciates art, imagination and diversity.',
      low: 'Pragmatic, prefers routine and proven methods. Values tradition and the concrete.',
      business: 'In business: brings innovation and creative thinking, but may sometimes lack focus on execution.',
    },
    C: {
      high: 'Organized, reliable, goal-oriented. Plans and finishes what they start.',
      low: 'Flexible, spontaneous, adaptable. Prefers improvising over planning.',
      business: 'In business: ensures quality and meeting deadlines, a pillar of reliability.',
    },
    E: {
      high: 'Sociable, energetic, expressive. Loves interacting and being at the center of action.',
      low: 'Reserved, thoughtful, independent. Prefers small group interactions or individual work.',
      business: 'In business: excellent for networking and representation, or deep focus and analysis.',
    },
    A: {
      high: 'Cooperative, empathetic, caring about others. Seeks harmony and avoids conflicts.',
      low: 'Competitive, direct, objective. Doesn\'t hesitate to firmly defend their positions.',
      business: 'In business: facilitates collaboration or drives performance and tough negotiations.',
    },
    N: {
      high: 'Sensitive to stress, feels emotions intensely. Can anticipate risks but also worry.',
      low: 'Emotionally stable, resilient, calm under pressure. Handles difficult situations well.',
      business: 'In business: emotional stability = calm in crisis, sensitivity = empathy and vigilance.',
    },
  },
};

// MBTI Data by language
const MBTI_DATA_FR = {
  dimensions: {
    'E/I': {
      E: { label: 'Extraversion', desc: 'Tire son énergie des interactions sociales' },
      I: { label: 'Introversion', desc: 'Tire son énergie de la réflexion intérieure' },
    },
    'S/N': {
      S: { label: 'Sensation', desc: 'Se concentre sur les faits concrets et le présent' },
      N: { label: 'Intuition', desc: 'Se concentre sur les possibilités et le futur' },
    },
    'T/F': {
      T: { label: 'Pensée', desc: 'Décide de manière logique et objective' },
      F: { label: 'Sentiment', desc: 'Décide selon les valeurs et l\'impact humain' },
    },
    'J/P': {
      J: { label: 'Jugement', desc: 'Préfère structure, planification et décisions' },
      P: { label: 'Perception', desc: 'Préfère flexibilité, spontanéité et options ouvertes' },
    },
  },
  types: {
    INTJ: {
      nickname: 'L\'Architecte',
      description: 'Penseur stratégique et indépendant avec un plan pour tout.',
      strengths: ['Vision stratégique', 'Indépendance', 'Détermination', 'Innovation'],
      inBusiness: 'Excellent pour la planification à long terme, l\'analyse de systèmes complexes et la définition de stratégies.',
    },
    INTP: {
      nickname: 'Le Logicien',
      description: 'Inventeur innovant avec une soif inextinguible de connaissance.',
      strengths: ['Analyse logique', 'Créativité', 'Objectivité', 'Adaptabilité'],
      inBusiness: 'Idéal pour résoudre des problèmes complexes, développer de nouveaux concepts et questionner les hypothèses.',
    },
    ENTJ: {
      nickname: 'Le Commandant',
      description: 'Leader audacieux, imaginatif et volontaire, trouvant ou créant un chemin.',
      strengths: ['Leadership', 'Efficacité', 'Vision', 'Décision'],
      inBusiness: 'Naturel pour diriger des équipes, piloter des projets ambitieux et transformer les organisations.',
    },
    ENTP: {
      nickname: 'L\'Innovateur',
      description: 'Penseur intelligent et curieux qui ne résiste pas à un défi intellectuel.',
      strengths: ['Créativité', 'Argumentation', 'Adaptabilité', 'Vision'],
      inBusiness: 'Parfait pour générer des idées, remettre en question le statu quo et explorer de nouvelles opportunités.',
    },
    INFJ: {
      nickname: 'L\'Avocat',
      description: 'Idéaliste calme et mystique, mais très inspirant et infatigable.',
      strengths: ['Intuition', 'Empathie', 'Détermination', 'Créativité'],
      inBusiness: 'Excellent pour comprendre les motivations profondes, conseiller et créer une vision inspirante.',
    },
    INFP: {
      nickname: 'Le Médiateur',
      description: 'Âme poétique et bienveillante, toujours prête à aider une bonne cause.',
      strengths: ['Empathie', 'Créativité', 'Dévouement', 'Ouverture d\'esprit'],
      inBusiness: 'Idéal pour les rôles créatifs, le conseil et les causes alignées avec ses valeurs.',
    },
    ENFJ: {
      nickname: 'Le Protagoniste',
      description: 'Leader charismatique et inspirant, capable de captiver son audience.',
      strengths: ['Charisme', 'Empathie', 'Leadership', 'Communication'],
      inBusiness: 'Naturel pour diriger des équipes, motiver et développer le potentiel des autres.',
    },
    ENFP: {
      nickname: 'Le Campagneur',
      description: 'Esprit libre, enthousiaste et créatif, trouvant toujours une raison de sourire.',
      strengths: ['Enthousiasme', 'Créativité', 'Communication', 'Empathie'],
      inBusiness: 'Excellent pour l\'innovation, la communication et l\'inspiration des équipes.',
    },
    ISTJ: {
      nickname: 'Le Logisticien',
      description: 'Individu pratique et fiable, dont la fiabilité ne peut être mise en doute.',
      strengths: ['Fiabilité', 'Organisation', 'Pragmatisme', 'Intégrité'],
      inBusiness: 'Pilier de stabilité, excellent pour la gestion, les processus et le respect des engagements.',
    },
    ISFJ: {
      nickname: 'Le Défenseur',
      description: 'Protecteur très dévoué et chaleureux, toujours prêt à défendre ses proches.',
      strengths: ['Dévouement', 'Fiabilité', 'Patience', 'Soutien'],
      inBusiness: 'Idéal pour le support, le service client et le maintien d\'un environnement de travail harmonieux.',
    },
    ESTJ: {
      nickname: 'L\'Exécutif',
      description: 'Administrateur excellent, inégalé pour gérer les choses ou les personnes.',
      strengths: ['Organisation', 'Leadership', 'Décision', 'Fiabilité'],
      inBusiness: 'Naturel pour la gestion de projets, l\'organisation d\'équipes et l\'exécution des plans.',
    },
    ESFJ: {
      nickname: 'Le Consul',
      description: 'Personne extraordinairement attentionnée, sociale et populaire, toujours prête à aider.',
      strengths: ['Sociabilité', 'Loyauté', 'Coopération', 'Praticité'],
      inBusiness: 'Excellent pour la coordination d\'équipe, les relations clients et la création d\'harmonie.',
    },
    ISTP: {
      nickname: 'Le Virtuose',
      description: 'Expérimentateur audacieux et pratique, maître de tout type d\'outils.',
      strengths: ['Praticité', 'Logique', 'Adaptabilité', 'Résolution de problèmes'],
      inBusiness: 'Idéal pour le dépannage, l\'analyse technique et les solutions pratiques.',
    },
    ISFP: {
      nickname: 'L\'Aventurier',
      description: 'Artiste flexible et charmant, toujours prêt à explorer et expérimenter quelque chose de nouveau.',
      strengths: ['Créativité', 'Sensibilité', 'Adaptabilité', 'Charme'],
      inBusiness: 'Parfait pour les rôles créatifs, le design et les environnements flexibles.',
    },
    ESTP: {
      nickname: 'L\'Entrepreneur',
      description: 'Personne intelligente, énergique et très perceptive, vivant vraiment à la limite.',
      strengths: ['Énergie', 'Praticité', 'Persuasion', 'Adaptabilité'],
      inBusiness: 'Excellent pour les ventes, la négociation et les environnements dynamiques.',
    },
    ESFP: {
      nickname: 'L\'Amuseur',
      description: 'Personne spontanée, énergique et enthousiaste – la vie n\'est jamais ennuyeuse autour d\'elle.',
      strengths: ['Enthousiasme', 'Praticité', 'Sociabilité', 'Adaptabilité'],
      inBusiness: 'Idéal pour les rôles de représentation, l\'animation et la création d\'une atmosphère positive.',
    },
  },
};

const MBTI_DATA_EN = {
  dimensions: {
    'E/I': {
      E: { label: 'Extraversion', desc: 'Gets energy from social interactions' },
      I: { label: 'Introversion', desc: 'Gets energy from inner reflection' },
    },
    'S/N': {
      S: { label: 'Sensing', desc: 'Focuses on concrete facts and the present' },
      N: { label: 'Intuition', desc: 'Focuses on possibilities and the future' },
    },
    'T/F': {
      T: { label: 'Thinking', desc: 'Decides logically and objectively' },
      F: { label: 'Feeling', desc: 'Decides based on values and human impact' },
    },
    'J/P': {
      J: { label: 'Judging', desc: 'Prefers structure, planning and decisions' },
      P: { label: 'Perceiving', desc: 'Prefers flexibility, spontaneity and open options' },
    },
  },
  types: {
    INTJ: {
      nickname: 'The Architect',
      description: 'Strategic and independent thinker with a plan for everything.',
      strengths: ['Strategic vision', 'Independence', 'Determination', 'Innovation'],
      inBusiness: 'Excellent for long-term planning, complex systems analysis and strategy definition.',
    },
    INTP: {
      nickname: 'The Logician',
      description: 'Innovative inventor with an unquenchable thirst for knowledge.',
      strengths: ['Logical analysis', 'Creativity', 'Objectivity', 'Adaptability'],
      inBusiness: 'Ideal for solving complex problems, developing new concepts and questioning assumptions.',
    },
    ENTJ: {
      nickname: 'The Commander',
      description: 'Bold, imaginative and strong-willed leader, finding or creating a way.',
      strengths: ['Leadership', 'Efficiency', 'Vision', 'Decision-making'],
      inBusiness: 'Natural for leading teams, driving ambitious projects and transforming organizations.',
    },
    ENTP: {
      nickname: 'The Debater',
      description: 'Smart and curious thinker who cannot resist an intellectual challenge.',
      strengths: ['Creativity', 'Argumentation', 'Adaptability', 'Vision'],
      inBusiness: 'Perfect for generating ideas, challenging the status quo and exploring new opportunities.',
    },
    INFJ: {
      nickname: 'The Advocate',
      description: 'Quiet and mystical idealist, yet very inspiring and tireless.',
      strengths: ['Intuition', 'Empathy', 'Determination', 'Creativity'],
      inBusiness: 'Excellent for understanding deep motivations, counseling and creating inspiring vision.',
    },
    INFP: {
      nickname: 'The Mediator',
      description: 'Poetic and kind soul, always ready to help a good cause.',
      strengths: ['Empathy', 'Creativity', 'Dedication', 'Open-mindedness'],
      inBusiness: 'Ideal for creative roles, counseling and causes aligned with their values.',
    },
    ENFJ: {
      nickname: 'The Protagonist',
      description: 'Charismatic and inspiring leader, able to captivate their audience.',
      strengths: ['Charisma', 'Empathy', 'Leadership', 'Communication'],
      inBusiness: 'Natural for leading teams, motivating and developing others\' potential.',
    },
    ENFP: {
      nickname: 'The Campaigner',
      description: 'Free spirit, enthusiastic and creative, always finding a reason to smile.',
      strengths: ['Enthusiasm', 'Creativity', 'Communication', 'Empathy'],
      inBusiness: 'Excellent for innovation, communication and inspiring teams.',
    },
    ISTJ: {
      nickname: 'The Logistician',
      description: 'Practical and reliable individual whose reliability cannot be questioned.',
      strengths: ['Reliability', 'Organization', 'Pragmatism', 'Integrity'],
      inBusiness: 'Pillar of stability, excellent for management, processes and honoring commitments.',
    },
    ISFJ: {
      nickname: 'The Defender',
      description: 'Very dedicated and warm protector, always ready to defend their loved ones.',
      strengths: ['Dedication', 'Reliability', 'Patience', 'Support'],
      inBusiness: 'Ideal for support, customer service and maintaining a harmonious work environment.',
    },
    ESTJ: {
      nickname: 'The Executive',
      description: 'Excellent administrator, unmatched in managing things or people.',
      strengths: ['Organization', 'Leadership', 'Decision-making', 'Reliability'],
      inBusiness: 'Natural for project management, team organization and plan execution.',
    },
    ESFJ: {
      nickname: 'The Consul',
      description: 'Extraordinarily caring, social and popular person, always ready to help.',
      strengths: ['Sociability', 'Loyalty', 'Cooperation', 'Practicality'],
      inBusiness: 'Excellent for team coordination, client relations and creating harmony.',
    },
    ISTP: {
      nickname: 'The Virtuoso',
      description: 'Bold and practical experimenter, master of all kinds of tools.',
      strengths: ['Practicality', 'Logic', 'Adaptability', 'Problem-solving'],
      inBusiness: 'Ideal for troubleshooting, technical analysis and practical solutions.',
    },
    ISFP: {
      nickname: 'The Adventurer',
      description: 'Flexible and charming artist, always ready to explore and experiment with something new.',
      strengths: ['Creativity', 'Sensitivity', 'Adaptability', 'Charm'],
      inBusiness: 'Perfect for creative roles, design and flexible environments.',
    },
    ESTP: {
      nickname: 'The Entrepreneur',
      description: 'Smart, energetic and very perceptive person, truly living on the edge.',
      strengths: ['Energy', 'Practicality', 'Persuasion', 'Adaptability'],
      inBusiness: 'Excellent for sales, negotiation and dynamic environments.',
    },
    ESFP: {
      nickname: 'The Entertainer',
      description: 'Spontaneous, energetic and enthusiastic person – life is never boring around them.',
      strengths: ['Enthusiasm', 'Practicality', 'Sociability', 'Adaptability'],
      inBusiness: 'Ideal for representation roles, hosting and creating a positive atmosphere.',
    },
  },
};

// Enneagram Data by language
const ENNEAGRAM_DATA_FR = {
  types: {
    'Type 1': {
      name: 'Le Perfectionniste',
      motivation: 'Être bon, avoir raison, améliorer les choses',
      fear: 'Être corrompu, mauvais ou imparfait',
      traits: ['Principes forts', 'Éthique', 'Autodiscipline', 'Sens du devoir'],
      inBusiness: 'Apporte rigueur, qualité et intégrité. Excellent pour les rôles nécessitant standards élevés.',
    },
    'Type 2': {
      name: 'L\'Altruiste',
      motivation: 'Être aimé, se sentir nécessaire, exprimer ses sentiments',
      fear: 'Être indésirable, non aimé',
      traits: ['Généreux', 'Empathique', 'Chaleureux', 'Soutenant'],
      inBusiness: 'Crée des relations fortes, excellent pour le service, le coaching et le support.',
    },
    'Type 3': {
      name: 'Le Battant',
      motivation: 'Réussir, être admiré, se distinguer des autres',
      fear: 'Être sans valeur, ne pas avoir de valeur intrinsèque',
      traits: ['Ambitieux', 'Compétent', 'Adaptable', 'Orienté succès'],
      inBusiness: 'Moteur de performance, excellent pour les rôles visibles et les objectifs ambitieux.',
    },
    'Type 4': {
      name: 'Le Romantique',
      motivation: 'Trouver son identité, exprimer son individualité',
      fear: 'Ne pas avoir d\'identité ou de signification personnelle',
      traits: ['Créatif', 'Sensible', 'Authentique', 'Introspectif'],
      inBusiness: 'Apporte créativité et profondeur, excellent pour les rôles artistiques et l\'innovation.',
    },
    'Type 5': {
      name: 'L\'Observateur',
      motivation: 'Comprendre le monde, être compétent, protéger son espace',
      fear: 'Être incompétent, inutile ou envahi',
      traits: ['Analytique', 'Objectif', 'Indépendant', 'Expert'],
      inBusiness: 'Apporte expertise et analyse approfondie, excellent pour la recherche et la stratégie.',
    },
    'Type 6': {
      name: 'Le Loyal',
      motivation: 'Avoir sécurité et soutien, tester les attitudes des autres',
      fear: 'Être sans soutien, sans guidance',
      traits: ['Responsable', 'Loyal', 'Prudent', 'Engagé'],
      inBusiness: 'Pilier de fiabilité, excellent pour la gestion des risques et le travail d\'équipe.',
    },
    'Type 7': {
      name: 'L\'Épicurien',
      motivation: 'Maintenir sa liberté et son bonheur, éviter la douleur',
      fear: 'Être privé, piégé dans la douleur',
      traits: ['Enthousiaste', 'Versatile', 'Spontané', 'Optimiste'],
      inBusiness: 'Génère énergie et innovation, excellent pour le brainstorming et les nouvelles initiatives.',
    },
    'Type 8': {
      name: 'Le Protecteur',
      motivation: 'Se protéger, contrôler son environnement, être fort',
      fear: 'Être blessé, contrôlé par les autres',
      traits: ['Confiant', 'Décisif', 'Protecteur', 'Direct'],
      inBusiness: 'Leader naturel, excellent pour les négociations difficiles et les décisions importantes.',
    },
    'Type 9': {
      name: 'Le Médiateur',
      motivation: 'Maintenir la paix intérieure et l\'harmonie',
      fear: 'Perte, fragmentation, séparation',
      traits: ['Réceptif', 'Apaisant', 'Stable', 'Accommodant'],
      inBusiness: 'Facilite la collaboration, excellent pour la médiation et le consensus.',
    },
  },
};

const ENNEAGRAM_DATA_EN = {
  types: {
    'Type 1': {
      name: 'The Perfectionist',
      motivation: 'To be good, to be right, to improve things',
      fear: 'Being corrupt, bad or imperfect',
      traits: ['Strong principles', 'Ethical', 'Self-disciplined', 'Sense of duty'],
      inBusiness: 'Brings rigor, quality and integrity. Excellent for roles requiring high standards.',
    },
    'Type 2': {
      name: 'The Helper',
      motivation: 'To be loved, to feel needed, to express feelings',
      fear: 'Being unwanted, unloved',
      traits: ['Generous', 'Empathetic', 'Warm', 'Supportive'],
      inBusiness: 'Creates strong relationships, excellent for service, coaching and support.',
    },
    'Type 3': {
      name: 'The Achiever',
      motivation: 'To succeed, to be admired, to stand out from others',
      fear: 'Being worthless, having no intrinsic value',
      traits: ['Ambitious', 'Competent', 'Adaptable', 'Success-oriented'],
      inBusiness: 'Performance driver, excellent for visible roles and ambitious goals.',
    },
    'Type 4': {
      name: 'The Individualist',
      motivation: 'To find identity, to express individuality',
      fear: 'Having no identity or personal significance',
      traits: ['Creative', 'Sensitive', 'Authentic', 'Introspective'],
      inBusiness: 'Brings creativity and depth, excellent for artistic roles and innovation.',
    },
    'Type 5': {
      name: 'The Investigator',
      motivation: 'To understand the world, to be competent, to protect space',
      fear: 'Being incompetent, useless or invaded',
      traits: ['Analytical', 'Objective', 'Independent', 'Expert'],
      inBusiness: 'Brings expertise and deep analysis, excellent for research and strategy.',
    },
    'Type 6': {
      name: 'The Loyalist',
      motivation: 'To have security and support, to test others\' attitudes',
      fear: 'Being without support, without guidance',
      traits: ['Responsible', 'Loyal', 'Cautious', 'Committed'],
      inBusiness: 'Pillar of reliability, excellent for risk management and teamwork.',
    },
    'Type 7': {
      name: 'The Enthusiast',
      motivation: 'To maintain freedom and happiness, to avoid pain',
      fear: 'Being deprived, trapped in pain',
      traits: ['Enthusiastic', 'Versatile', 'Spontaneous', 'Optimistic'],
      inBusiness: 'Generates energy and innovation, excellent for brainstorming and new initiatives.',
    },
    'Type 8': {
      name: 'The Challenger',
      motivation: 'To protect oneself, to control environment, to be strong',
      fear: 'Being hurt, controlled by others',
      traits: ['Confident', 'Decisive', 'Protective', 'Direct'],
      inBusiness: 'Natural leader, excellent for tough negotiations and important decisions.',
    },
    'Type 9': {
      name: 'The Peacemaker',
      motivation: 'To maintain inner peace and harmony',
      fear: 'Loss, fragmentation, separation',
      traits: ['Receptive', 'Calming', 'Stable', 'Accommodating'],
      inBusiness: 'Facilitates collaboration, excellent for mediation and consensus.',
    },
  },
};

// Schwartz Values by language
const SCHWARTZ_DATA_FR = {
  values: {
    'Autonomie': {
      description: 'Indépendance de pensée et d\'action – choisir, créer, explorer.',
      inBusiness: 'Recherche de liberté dans les décisions et les méthodes de travail.',
    },
    'Stimulation': {
      description: 'Excitation, nouveauté et défis dans la vie.',
      inBusiness: 'Besoin de variété et de nouveaux projets pour rester engagé.',
    },
    'Hédonisme': {
      description: 'Plaisir et gratification sensorielle pour soi-même.',
      inBusiness: 'Importance de l\'environnement de travail agréable et des récompenses.',
    },
    'Réussite': {
      description: 'Succès personnel par la démonstration de compétence.',
      inBusiness: 'Motivation par les objectifs, la reconnaissance et l\'avancement.',
    },
    'Pouvoir': {
      description: 'Statut social, prestige, contrôle sur les personnes et ressources.',
      inBusiness: 'Attrait pour les positions d\'influence et de responsabilité.',
    },
    'Sécurité': {
      description: 'Sûreté, harmonie et stabilité de la société et des relations.',
      inBusiness: 'Préférence pour la stabilité de l\'emploi et l\'environnement prévisible.',
    },
    'Conformité': {
      description: 'Restriction des actions susceptibles de nuire aux autres ou de violer les normes.',
      inBusiness: 'Respect des règles, procédures et hiérarchie.',
    },
    'Tradition': {
      description: 'Respect et engagement envers les coutumes et idées culturelles.',
      inBusiness: 'Valorisation de l\'histoire de l\'entreprise et des pratiques établies.',
    },
    'Bienveillance': {
      description: 'Préserver et améliorer le bien-être des proches.',
      inBusiness: 'Importance des relations de qualité et du soutien aux collègues.',
    },
    'Universalisme': {
      description: 'Compréhension, tolérance et protection du bien-être de tous.',
      inBusiness: 'Engagement pour la responsabilité sociale et l\'équité.',
    },
  },
};

const SCHWARTZ_DATA_EN = {
  values: {
    'Autonomie': {
      description: 'Independence of thought and action – choosing, creating, exploring.',
      inBusiness: 'Seeking freedom in decisions and work methods.',
    },
    'Stimulation': {
      description: 'Excitement, novelty and challenges in life.',
      inBusiness: 'Need for variety and new projects to stay engaged.',
    },
    'Hédonisme': {
      description: 'Pleasure and sensory gratification for oneself.',
      inBusiness: 'Importance of pleasant work environment and rewards.',
    },
    'Réussite': {
      description: 'Personal success through demonstrating competence.',
      inBusiness: 'Motivated by goals, recognition and advancement.',
    },
    'Pouvoir': {
      description: 'Social status, prestige, control over people and resources.',
      inBusiness: 'Attracted to positions of influence and responsibility.',
    },
    'Sécurité': {
      description: 'Safety, harmony and stability of society and relationships.',
      inBusiness: 'Preference for job stability and predictable environment.',
    },
    'Conformité': {
      description: 'Restriction of actions likely to harm others or violate norms.',
      inBusiness: 'Respect for rules, procedures and hierarchy.',
    },
    'Tradition': {
      description: 'Respect and commitment to cultural customs and ideas.',
      inBusiness: 'Values company history and established practices.',
    },
    'Bienveillance': {
      description: 'Preserving and enhancing the welfare of close others.',
      inBusiness: 'Importance of quality relationships and supporting colleagues.',
    },
    'Universalisme': {
      description: 'Understanding, tolerance and protection of all people\'s welfare.',
      inBusiness: 'Commitment to social responsibility and equity.',
    },
  },
};

// VIA Strengths by language
const VIA_DATA_FR = {
  categories: {
    'Sagesse': ['Créativité', 'Curiosité', 'Jugement', 'Amour apprentissage', 'Perspective'],
    'Courage': ['Bravoure', 'Persévérance', 'Honnêteté', 'Vitalité'],
    'Humanité': ['Amour', 'Gentillesse', 'Intelligence sociale'],
    'Justice': ['Esprit d\'équipe', 'Équité', 'Leadership'],
    'Tempérance': ['Pardon', 'Humilité', 'Prudence', 'Autorégulation'],
    'Transcendance': ['Appréciation beauté', 'Gratitude', 'Espoir', 'Humour', 'Spiritualité'],
  },
  descriptions: {
    'Créativité': 'Penser à des façons nouvelles et productives de faire les choses.',
    'Curiosité': 'S\'intéresser à toutes les expériences en cours pour elles-mêmes.',
    'Jugement': 'Réfléchir aux choses et les examiner de tous les côtés.',
    'Amour apprentissage': 'Maîtriser de nouvelles compétences, sujets et savoirs.',
    'Perspective': 'Être capable de fournir de sages conseils aux autres.',
    'Bravoure': 'Ne pas reculer devant la menace, le défi ou la douleur.',
    'Persévérance': 'Finir ce qu\'on commence malgré les obstacles.',
    'Honnêteté': 'Dire la vérité et se présenter de manière authentique.',
    'Vitalité': 'Approcher la vie avec enthousiasme et énergie.',
    'Amour': 'Valoriser les relations proches avec les autres.',
    'Gentillesse': 'Faire des faveurs et des bonnes actions pour les autres.',
    'Intelligence sociale': 'Être conscient des motivations et sentiments des autres.',
    'Esprit d\'équipe': 'Bien travailler comme membre d\'un groupe.',
    'Équité': 'Traiter toutes les personnes de la même manière selon les notions d\'équité.',
    'Leadership': 'Organiser les activités de groupe et veiller à ce qu\'elles se réalisent.',
    'Pardon': 'Pardonner à ceux qui ont fait du mal.',
    'Humilité': 'Laisser ses accomplissements parler d\'eux-mêmes.',
    'Prudence': 'Être prudent dans ses choix, ne pas prendre de risques inutiles.',
    'Autorégulation': 'Réguler ce qu\'on ressent et fait.',
    'Appréciation beauté': 'Remarquer et apprécier la beauté et l\'excellence.',
    'Gratitude': 'Être conscient et reconnaissant des bonnes choses.',
    'Espoir': 'S\'attendre au meilleur et travailler pour l\'atteindre.',
    'Humour': 'Aimer rire et taquiner, apporter des sourires aux autres.',
    'Spiritualité': 'Avoir des croyances cohérentes sur un but supérieur.',
  },
};

const VIA_DATA_EN = {
  categories: {
    'Wisdom': ['Creativity', 'Curiosity', 'Judgment', 'Love of Learning', 'Perspective'],
    'Courage': ['Bravery', 'Perseverance', 'Honesty', 'Zest'],
    'Humanity': ['Love', 'Kindness', 'Social Intelligence'],
    'Justice': ['Teamwork', 'Fairness', 'Leadership'],
    'Temperance': ['Forgiveness', 'Humility', 'Prudence', 'Self-Regulation'],
    'Transcendence': ['Appreciation of Beauty', 'Gratitude', 'Hope', 'Humor', 'Spirituality'],
  },
  descriptions: {
    'Creativity': 'Thinking of new and productive ways to do things.',
    'Curiosity': 'Taking an interest in all ongoing experiences for their own sake.',
    'Judgment': 'Thinking things through and examining them from all sides.',
    'Love of Learning': 'Mastering new skills, topics and bodies of knowledge.',
    'Perspective': 'Being able to provide wise counsel to others.',
    'Bravery': 'Not shrinking from threat, challenge or pain.',
    'Perseverance': 'Finishing what one starts despite obstacles.',
    'Honesty': 'Telling the truth and presenting oneself authentically.',
    'Zest': 'Approaching life with enthusiasm and energy.',
    'Love': 'Valuing close relationships with others.',
    'Kindness': 'Doing favors and good deeds for others.',
    'Social Intelligence': 'Being aware of the motives and feelings of others.',
    'Teamwork': 'Working well as a member of a group.',
    'Fairness': 'Treating all people the same according to notions of fairness.',
    'Leadership': 'Organizing group activities and seeing that they happen.',
    'Forgiveness': 'Forgiving those who have done wrong.',
    'Humility': 'Letting accomplishments speak for themselves.',
    'Prudence': 'Being careful about choices, not taking undue risks.',
    'Self-Regulation': 'Regulating what one feels and does.',
    'Appreciation of Beauty': 'Noticing and appreciating beauty and excellence.',
    'Gratitude': 'Being aware of and thankful for good things.',
    'Hope': 'Expecting the best and working to achieve it.',
    'Humor': 'Liking to laugh and tease, bringing smiles to others.',
    'Spirituality': 'Having coherent beliefs about a higher purpose.',
  },
};

// Export functions that return data based on language
export const getDISC_DATA = (language = 'fr') => language === 'en' ? DISC_DATA_EN : DISC_DATA_FR;
export const getBIGFIVE_DATA = (language = 'fr') => language === 'en' ? BIGFIVE_DATA_EN : BIGFIVE_DATA_FR;
export const getMBTI_DATA = (language = 'fr') => language === 'en' ? MBTI_DATA_EN : MBTI_DATA_FR;
export const getENNEAGRAM_DATA = (language = 'fr') => language === 'en' ? ENNEAGRAM_DATA_EN : ENNEAGRAM_DATA_FR;
export const getSCHWARTZ_DATA = (language = 'fr') => language === 'en' ? SCHWARTZ_DATA_EN : SCHWARTZ_DATA_FR;
export const getVIA_DATA = (language = 'fr') => language === 'en' ? VIA_DATA_EN : VIA_DATA_FR;

// For backward compatibility, also export the FR versions as defaults
export const DISC_DATA = DISC_DATA_FR;
export const BIGFIVE_DATA = BIGFIVE_DATA_FR;
export const MBTI_DATA = MBTI_DATA_FR;
export const ENNEAGRAM_DATA = ENNEAGRAM_DATA_FR;
export const SCHWARTZ_DATA = SCHWARTZ_DATA_FR;
export const VIA_DATA = VIA_DATA_FR;

// Test introductions - now using translations from the translation files
// This is kept for backward compatibility but should use t.tests instead
export const TEST_INTROS = {
  disc: {
    title: 'DISC',
    description: 'Le modèle DISC évalue votre style comportemental au travail selon 4 dimensions.',
  },
  bigfive: {
    title: 'Big Five (OCEAN)',
    description: 'Les Big Five représentent le modèle de personnalité le plus validé scientifiquement.',
  },
  via: {
    title: 'Forces de Caractère (VIA)',
    description: 'L\'inventaire VIA identifie vos 24 forces de caractère.',
  },
  schwartz: {
    title: 'Valeurs Personnelles (Schwartz)',
    description: 'Les valeurs de Schwartz révèlent ce qui motive réellement vos décisions.',
  },
  enneagram: {
    title: 'Ennéagramme',
    description: 'L\'Ennéagramme identifie 9 types de personnalité basés sur les motivations profondes.',
  },
  mbti: {
    title: 'MBTI (16 Personnalités)',
    description: 'Le MBTI identifie votre type parmi 16 profils basés sur vos préférences cognitives.',
  },
};
